import { z } from 'zod';
import { shipById } from './ships.js';
/**
 * Crew profiles (`/crew/<slug>`) and the decorations (ribbons/medals/
 * ensigns) accomplishments are drawn as. Kept in its own file so
 * `contracts.ts` (which needs `decorationKindSchema`/`decorationCategorySchema`/
 * `starThresholdsSchema` on `accomplishmentSchema`) can import from here
 * without this file ever needing anything back from `contracts.ts`.
 */
// ---------------------------------------------------------------------------
// decoration kind / category
// ---------------------------------------------------------------------------
/** How an accomplishment is drawn on a profile. */
export const decorationKindSchema = z.enum(['ribbon', 'medal', 'ensign']);
/** Fixed category list (real SQL CHECK on `accomplishments.category` — see api/src/db/schema.ts). */
export const decorationCategorySchema = z.enum([
    'port_battles',
    'fleet_actions',
    'seamanship',
    'shipwrighting',
    'trade',
    'campaigns',
    'service',
]);
/** Category id paired with its display label, in the fixed order managers see them. */
export const DECORATION_CATEGORIES = [
    { value: 'port_battles', label: 'Port battles' },
    { value: 'fleet_actions', label: 'Fleet actions' },
    { value: 'seamanship', label: 'Seamanship' },
    { value: 'shipwrighting', label: 'Shipwrighting' },
    { value: 'trade', label: 'Trade' },
    { value: 'campaigns', label: 'Campaigns' },
    { value: 'service', label: 'Service to the clan' },
];
// ---------------------------------------------------------------------------
// star thresholds / star tier
// ---------------------------------------------------------------------------
/**
 * Officer-set service-star thresholds for a repeatable accomplishment:
 * [bronze, silver, gold] approved-claim counts, strictly ascending and
 * positive — or an empty array for "no stars configured". Matches the
 * database's `star_thresholds` CHECK (a comma list of exactly 3 positive
 * ints, or '').
 */
export const starThresholdsSchema = z
    .array(z.number().int().positive())
    .refine((v) => v.length === 0 || v.length === 3, {
    message: 'Star thresholds must be empty or exactly 3 values (bronze, silver, gold).',
})
    .refine((v) => v.length < 3 || (v[0] < v[1] && v[1] < v[2]), {
    message: 'Star thresholds must be strictly ascending.',
});
export const starTierSchema = z.enum(['none', 'bronze', 'silver', 'gold']);
/**
 * Pure: the highest star tier `count` approved claims reach against
 * `thresholds` ([bronze, silver, gold]), and the next threshold to show
 * progress toward. An empty `thresholds` (service stars not configured for
 * this accomplishment) always reports 'none' with no next target.
 */
export function starTier(count, thresholds) {
    if (thresholds.length < 3)
        return { tier: 'none', next: null };
    const [bronze, silver, gold] = thresholds;
    if (count >= gold)
        return { tier: 'gold', next: null };
    if (count >= silver)
        return { tier: 'silver', next: gold };
    if (count >= bronze)
        return { tier: 'bronze', next: silver };
    return { tier: 'none', next: bronze };
}
// ---------------------------------------------------------------------------
// member-set profile fields
// ---------------------------------------------------------------------------
const flagshipName = z.string().trim().min(1).max(40);
const crewName = z.string().trim().min(1).max(60);
const motto = z.string().trim().max(160);
/**
 * A flag is either one of the existing clan flags under `/art/flags` or a
 * flag uploaded through POST /api/flags, served back at `/api/flags/<id>`.
 * Lives here (not contracts.ts) so `updateProfileInputSchema` below can use
 * it without contracts.ts's one-way dependency on this file reversing —
 * contracts.ts re-exports it for its own roster schemas.
 */
export const rosterFlagSchema = z
    .string()
    .refine((f) => /^\/art\/flags\/[\w.-]+\.webp$/.test(f) || /^\/api\/flags\/[0-9a-f-]{36}$/.test(f), {
    message: 'Flag must be an existing clan flag or an uploaded flag image.',
});
/**
 * PATCH /api/me/profile — the signed-in member's own crew name, flag, motto,
 * favourite ship and flagship name. `name`/`flag` are also what an
 * officer/owner may set on someone else via the manage endpoints — same
 * rules, so the same schema validates both call sites.
 */
export const updateProfileInputSchema = z
    .object({
    name: crewName,
    flag: rosterFlagSchema,
    motto,
    favouriteShip: z
        .string()
        .nullable()
        .refine((id) => id === null || shipById(id) !== undefined, { message: 'Unknown ship id.' }),
    flagshipName: flagshipName.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
/**
 * PATCH /api/crew/:slug/manage — the profile fields a manager may set for a
 * crew member from their admin page in the cave. Name, rank, section and flag
 * go through PATCH /api/roster/:id; role through PATCH /api/members/:id/role.
 */
export const updateCrewMemberProfileInputSchema = z
    .object({
    motto,
    favouriteShip: z
        .string()
        .nullable()
        .refine((id) => id === null || shipById(id) !== undefined, { message: 'Unknown ship id.' }),
    flagshipName: flagshipName.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update.' });
// ---------------------------------------------------------------------------
// ship plans
// ---------------------------------------------------------------------------
/** A schematic shown faintly behind a ship's section on a profile — admin-managed, keyed by ship id. */
export const shipPlanSchema = z.object({
    shipId: z.string(),
    /** An uploaded image path `/api/flags/<uuid>`. */
    image: z.string(),
    credit: z.string(),
});
/** PUT /api/ships/:shipId/plan — manager only. */
export const putShipPlanInputSchema = z.object({
    image: z.string(),
    credit: z.string().trim().max(200).default(''),
});
// ---------------------------------------------------------------------------
// crew profile (GET /api/crew/:slug — public)
// ---------------------------------------------------------------------------
const profileShipSchema = z.object({
    id: z.string(),
    name: z.string(),
    rate: z.number().int(),
    decks: z.number().int(),
    guns: z.number().int(),
    plan: z.object({ image: z.string(), credit: z.string() }).nullable(),
});
/** One accomplishment this member has earned (at least one approved claim). */
export const earnedDecorationSchema = z.object({
    accomplishmentId: z.string(),
    title: z.string(),
    description: z.string(),
    art: z.string().nullable(),
    firstEarnedAt: z.string().datetime(),
    /** Approved-claim count — always 1 for a non-repeatable accomplishment. */
    count: z.number().int(),
    stars: z.object({ tier: starTierSchema, next: z.number().int().nullable() }),
});
/** A repeatable accomplishment this member has approved claims on but hasn't maxed out yet. */
export const closestToEarningSchema = z.object({
    accomplishmentId: z.string(),
    title: z.string(),
    description: z.string(),
    art: z.string().nullable(),
    count: z.number().int(),
    next: z.number().int(),
});
const collectionCategorySchema = z.object({
    category: decorationCategorySchema,
    label: z.string(),
    earned: z.number().int(),
    total: z.number().int(),
});
/**
 * GET /api/crew/:slug — public, 404 for an unknown slug or a non-approved
 * user. Never carries discordUserId, session or status data, or any private
 * claim field (reviewNote, evidenceUrl, note, claim ids) — see
 * buildCrewProfile's own key-set tests.
 */
export const crewProfileSchema = z.object({
    slug: z.string(),
    name: z.string(),
    /** Roster subtitle if this member has one, else their role label. */
    rank: z.string(),
    flag: z.string(),
    /** A short line the member (or an officer) set on their profile — '' if none. */
    motto: z.string(),
    joinedAt: z.string().datetime(),
    favouriteShip: profileShipSchema.nullable(),
    flagshipName: z.string().nullable(),
    stats: z.object({
        decorations: z.number().int(),
        points: z.number().int(),
        /** 1-based rank among approved members by points desc; ties share (competition ranking: 1, 1, 3). */
        leaderboardPosition: z.number().int(),
    }),
    medals: z.array(earnedDecorationSchema),
    ribbons: z.array(earnedDecorationSchema),
    ensigns: z.array(earnedDecorationSchema),
    collection: z.object({
        earned: z.number().int(),
        total: z.number().int(),
        categories: z.array(collectionCategorySchema),
    }),
    /** Up to 3 repeatables with count > 0 and a next threshold, highest count/next first. */
    closestToEarning: z.array(closestToEarningSchema),
});
//# sourceMappingURL=profiles.js.map